'use client';

import { FC, useMemo } from 'react';

import { isEmptyString } from '@core/Utils/Strings/EmptyString';
import { Grid } from '@gds/Grid/Grid';
import { ImageElement } from '@gds/ImageElement/ImageElement';
import { ImageElementData } from '@gds/ImageElement/ImageElement.types';
import useBreakpoint from '@gds/Pagination/Hooks/UseBreakpoint.hook';
import { ReviewRating } from '@gds/ReviewRating/ReviewRating';
import { Typography } from '@gds/Typography/Typography';
import { isEmptyRichText } from '@kentico/Utils/IsEmptyRichText';
import { RichText } from '@page-builder/Components/RichText/RichText';
import { checkHasCta } from '@page-builder/Utils/HasCta';
import cn from 'classnames';

import Cta from '../FloatingCta/Cta';

import styles from './GuideSection.module.css';

type Props = {
  cmsData: any;
};

const GuideSection: FC<Props> = ({ cmsData }) => {
  const {
    title: { value: guideSectionTitle },
    subtitle: { value: launchDate },
    content: richText,
    rating: { value: ratingValue },
    cta: { linkedItems: ctaItems },
    image,
  } = cmsData.elements;

  const imageElement = image.value[0];
  const imageElementData: ImageElementData = {
    url: imageElement.url,
    description: imageElement.description,
  };

  const {
    breakpoints: { isDesktop, isTablet },
  } = useBreakpoint();

  const shouldShowRichText = !isEmptyRichText(richText.value);
  const shouldShowCtaBlock = checkHasCta(cmsData) ? true : false;

  const ratingSize = useMemo(() => {
    if (isDesktop) {
      return 'L';
    }
    if (isTablet) {
      return 'M';
    }
    return 'S';
  }, [isDesktop, isTablet]);

  return (
    <div className={cn(styles.guide_section, 'blogCmpnt', 'guideSectionModule')}>
      <div className={styles.header}>
        <Grid.Container>
          <Grid.Row justify="center">
            <Grid.Col
              align="bottom"
              width={{
                mobile: 12,
                tablet: 6,
                desktop: 4,
              }}
              order={{
                mobile: 0,
                tablet: 1,
                desktop: 1,
              }}
            >
              <div className={styles.carInfo}>
                <Typography variant="h2" className={styles.title}>
                  {guideSectionTitle}
                </Typography>
                {!isEmptyString(launchDate) && (
                  <div className={styles.date}>
                    <Typography variant="body3">{launchDate}</Typography>
                  </div>
                )}
              </div>
            </Grid.Col>

            <Grid.Col
              width={{
                mobile: 12,
                tablet: 6,
                desktop: 4,
              }}
              className={styles.imageSection}
            >
              <ImageElement imageElementData={imageElementData} ratio="4:3" />
              {ratingValue && (
                <ReviewRating score={ratingValue} size={ratingSize} className={styles.rating} />
              )}
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
      </div>

      {shouldShowRichText && (
        <Grid.Container>
          <Grid.Row justify="center">
            <Grid.Col
              width={{
                mobile: 12,
                tablet: 12,
                desktop: 8,
              }}
            >
              <RichText richText={richText.value} className={styles.richTextElement} />
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
      )}

      {shouldShowCtaBlock && (
        <Grid.Container>
          <Grid.Row justify="center">
            <Grid.Col
              width={{
                mobile: 12,
                tablet: 12,
                desktop: ctaItems.length > 1 ? 8 : 4,
              }}
              className={styles.ctaBlockWrapper}
            >
              <div className={styles.ctaBlock}>
                {cmsData.elements.cta.linkedItems
                  .slice(0, 2)
                  .reverse()
                  .map((ctaData: any, index: number) => (
                    <Cta
                      variant={index % 2 ? 'outlined' : undefined}
                      key={ctaData.system.id}
                      fullWidth
                      ctaData={ctaData}
                    />
                  ))}
              </div>
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
      )}
    </div>
  );
};

export default GuideSection;
